import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import type { Article } from '../../../types/article';
import { timeAgo } from '../../../utils/timeAgo';

const categoryTag: Record<string, string> = {
  Warzone: 'WZ',
  Multiplayer: 'MP',
  Zombies: 'ZM',
  Esports: 'CDL',
  Community: 'COM',
  Leaks: 'LEAK',
};

function Tag({ label }: { label: string }) {
  return (
    <span className="inline-block font-['Barlow_Condensed'] font-bold text-xs tracking-wider text-white bg-[#2a2a2a] border border-[#3a3a3a] px-2 py-0.5">
      {label}
    </span>
  );
}

function HorizontalCard({ article }: { article: Article }) {
  const slug = article.slug || article.id;
  return (
    <Link
      to={`/news/${slug}`}
      className="group flex gap-4 cursor-pointer border-b border-[#1e1e1e] pb-5 last:border-0 last:pb-0"
    >
      {/* Thumbnail */}
      <div className="shrink-0 overflow-hidden" style={{ width: '120px', height: '80px' }}>
        {article.image ? (
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-[#1a1a1a] flex items-center justify-center">
            <i className="ri-image-line text-[#333]" />
          </div>
        )}
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[#666] text-xs font-['Barlow_Condensed'] font-bold tracking-wider">
            {timeAgo(article.date)}
          </span>
        </div>
        <h3 className="font-['Barlow_Condensed'] font-black text-white text-sm leading-tight uppercase group-hover:text-[#00ff41] transition-colors mb-2 line-clamp-2">
          {article.title}
        </h3>
        <div className="flex items-center gap-1.5">
          <Tag label={categoryTag[article.category] ?? article.category.toUpperCase()} />
        </div>
      </div>
    </Link>
  );
}

export default function LatestNews() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    const fetch_ = async () => {
      const { data } = await supabase
        .from('articles')
        .select('*')
        .eq('published', true)
        .order('date', { ascending: false })
        .range(3, 100);
      setArticles((data as Article[]) ?? []);
      setLoading(false);
    };
    fetch_();
  }, []);

  if (loading) {
    return (
      <section className="bg-[#0f0f0f] py-10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-center py-12 text-[#555]">
          <i className="ri-loader-4-line animate-spin text-xl mr-2" /> Loading...
        </div>
      </section>
    );
  }

  if (articles.length === 0) return null;

  const visible = articles.slice(0, visibleCount);

  return (
    <section className="bg-[#0f0f0f] py-10 border-t border-[#1e1e1e]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <h2 className="font-['Barlow_Condensed'] font-black text-white text-2xl uppercase tracking-widest mb-6 border-b border-[#1e1e1e] pb-4">
          MORE NEWS
        </h2>

        {/* 3-column horizontal card grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-0">
          {visible.map((article) => (
            <HorizontalCard key={article.id} article={article} />
          ))}
        </div>

        {/* View More */}
        {visibleCount < articles.length && (
          <div className="mt-10 flex justify-center">
            <button
              onClick={() => setVisibleCount((c) => c + 6)}
              className="font-['Barlow_Condensed'] font-black text-sm tracking-widest text-white border border-[#444] px-12 py-3 hover:bg-white hover:text-black transition-all cursor-pointer whitespace-nowrap"
            >
              VIEW MORE
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
